import { API_BASE, DEFAULT_HOMEPAGE_SLUG } from '../../constants'
import { normalizePage, normalizeSettingsHeroSlide } from '../../normalizers'
import type { CmsPage as CmsContent } from '../../../cms/types'
import { enrichStorefrontPageRuntimeBlocks } from '../../../cms/storefrontRuntime'
import type { Slide } from '../../types'
import { FetchPolicyError, fetchWithPolicy } from '../../../shared/api/fetchPolicy'
import {
  parseRuntimeHomeSettings,
  resolveHomepageSlugFromRuntimeSettings,
  type RuntimeHomeSettings,
  type RuntimeLink,
} from '../../runtimeSettings'
import { DEFAULT_SHOP_LOCALE, inferLocaleFromPathname } from '../../../i18n/shopLocale'

export type { RuntimeHomeSettings, RuntimeLink }

type RuntimeSettingsPayload = {
  slides: Slide[]
  homepageSlug: string
  status: string
  settings: RuntimeHomeSettings
}

let settingsCache: { value: RuntimeSettingsPayload; expiresAt: number } | null = null
let settingsInFlight: Promise<RuntimeSettingsPayload> | null = null
const SETTINGS_CACHE_TTL_MS = 30_000

const resolveRuntimeLocale = () => {
  if (typeof window === 'undefined') return DEFAULT_SHOP_LOCALE
  return inferLocaleFromPathname(window.location.pathname) ?? DEFAULT_SHOP_LOCALE
}

const appendRuntimeCacheBust = (path: string) => {
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}runtimeTs=${Date.now()}`
}

export const fetchHomePageBySlug = async (slugValue: string): Promise<CmsContent> => {
  const trimmed = slugValue.trim()
  const cleanSlug = trimmed === '/' ? '/' : trimmed.replace(/^\/+/, '')
  if (!cleanSlug && trimmed !== '/') {
    throw new Error('Homepage non trovata. Crea o pubblica la pagina.')
  }

  const fetchPage = async (targetPath: string, headers?: Record<string, string>) => {
    let res: Response
    try {
      res = await fetchWithPolicy({
        request: targetPath,
        init: { headers, cache: 'no-store' },
      })
    } catch (error) {
      if (error instanceof FetchPolicyError) {
        throw new Error('Errore caricamento pagina', { cause: error })
      }
      throw error
    }
    if (!res.ok) {
      const text = await res.text()
      throw new Error(text || `Errore ${res.status}`)
    }
    return res.json()
  }

  const locale = resolveRuntimeLocale()
  const localeSuffix = `?locale=${encodeURIComponent(locale)}`
  const pagePath =
    cleanSlug === '/'
      ? `${API_BASE}/pages?homepage=1&locale=${encodeURIComponent(locale)}`
      : `${API_BASE}/pages/${encodeURIComponent(cleanSlug)}${localeSuffix}`
  const data = await fetchPage(
    appendRuntimeCacheBust(pagePath),
  )

  const rawPage = data?.page
  if (!rawPage || typeof rawPage !== 'object' || Array.isArray(rawPage)) {
    throw new Error('Homepage non trovata. Crea o pubblica la pagina.')
  }

  const raw = rawPage as Record<string, unknown>
  const page = normalizePage(raw)
  return enrichStorefrontPageRuntimeBlocks({
    page,
    context: {
      locale,
      pageId: page._id,
      pageSlug: page.slug,
    },
  })
}

export const fetchSettingsSlidesAndHomepage = async (): Promise<RuntimeSettingsPayload> => {
  const now = Date.now()
  if (settingsCache && settingsCache.expiresAt > now) {
    return settingsCache.value
  }
  if (settingsInFlight) {
    return settingsInFlight
  }

  settingsInFlight = (async () => {
  let res: Response
  try {
    res = await fetchWithPolicy({
      request: appendRuntimeCacheBust(`${API_BASE}/settings/public`),
      init: { cache: 'no-store' },
      retries: 0,
    })
  } catch (error) {
    if (error instanceof FetchPolicyError) {
      throw new Error('API non raggiungibile', { cause: error })
    }
    throw error
  }
  if (!res.ok) throw new Error('API non raggiungibile')

  const data = await res.json()
  const rawSlides = Array.isArray(data?.hero?.slides)
    ? (data.hero.slides as Array<Record<string, unknown>>)
    : []
  const remoteSlides: Slide[] = rawSlides.map((slide) => normalizeSettingsHeroSlide(slide))

  const status = remoteSlides.length
    ? 'Slide caricate dal CMS.'
    : 'Nessuna slide salvata: uso fallback.'

  const homepageSlug =
    resolveHomepageSlugFromRuntimeSettings(data?.settings, DEFAULT_HOMEPAGE_SLUG) ||
    DEFAULT_HOMEPAGE_SLUG
  const settings = parseRuntimeHomeSettings(data?.settings)

    const payload: RuntimeSettingsPayload = {
    slides: remoteSlides,
    homepageSlug,
    status,
    settings,
    }
    settingsCache = {
      value: payload,
      expiresAt: Date.now() + SETTINGS_CACHE_TTL_MS,
    }
    return payload
  })()

  try {
    return await settingsInFlight
  } finally {
    settingsInFlight = null
  }
}
